// Plattform – „Funktionen & Vorlagen" je Kunde: Paket (Voreinstellung), Vorlagen-Kategorien und Module/Bausteine
// ein- und ausschalten; globale Vorlagen nur für bestimmte Kunden freigeben. Durchgesetzt wird auf dem Server.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import {
  qkFeatures, useFeatureCatalog, useGlobalTemplates, useTenantFeatures, type FeatureCatalog, type GlobalTemplateAccess, type Toggles,
} from '../../api/features';
import type { TenantListItem } from '../../api/types';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/toast';
import { Dialog, Field, Spinner } from '../../components/ui';
import '../../styles/features.css';

export interface FeatureValue { preset: string | null; modules: Toggles; categories: Toggles }

const tr = (n: { de: string; en: string }, lang: string) => (lang.startsWith('en') ? n.en : n.de);

/** Schalter aus einem Paket. */
export function fromPreset(catalog: FeatureCatalog, key: string): FeatureValue {
  const p = catalog.presets.find((x) => x.key === key);
  return { preset: key, modules: { ...(p?.modules ?? {}) }, categories: { ...(p?.categories ?? {}) } };
}

/** Passendes Paket zur Branche („andere" = Alles). */
export const presetForIndustry = (catalog: FeatureCatalog, industry: string) => catalog.presets.find((p) => p.industry === industry)?.key ?? 'alles';

/** Paket-Auswahl, Vorlagen-Kategorien und Funktionen als Schalter. */
export function FeatureToggles({ catalog, value, onChange }: { catalog: FeatureCatalog; value: FeatureValue; onChange: (v: FeatureValue) => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const on = (m: Toggles, k: string) => m[k] !== false;
  const setModule = (k: string, v: boolean) => onChange({ ...value, preset: null, modules: { ...value.modules, [k]: v } });
  const setCategory = (k: string, v: boolean) => onChange({ ...value, preset: null, categories: { ...value.categories, [k]: v } });
  // Paket erkennen, wenn die Schalter genau einem Paket entsprechen
  const matching = catalog.presets.find((p) => catalog.modules.every((m) => on(p.modules, m.key) === on(value.modules, m.key))
    && catalog.categories.every((c) => on(p.categories, c.key) === on(value.categories, c.key)));
  const presetShown = value.preset ?? matching?.key ?? '';
  return (
    <div className="col" style={{ gap: 14 }} data-testid="feature-toggles">
      <Field label={t('features.preset')} hint={t('features.presetHint')}>
        <select value={presetShown} onChange={(e) => e.target.value && onChange(fromPreset(catalog, e.target.value))} aria-label={t('features.preset')}>
          {!presetShown && <option value="">{t('features.custom')}</option>}
          {catalog.presets.map((p) => <option key={p.key} value={p.key}>{tr(p.name, lang)}</option>)}
        </select>
      </Field>
      <fieldset className="feat-group">
        <legend>{t('features.templates')}</legend>
        <div className="feat-grid">
          {catalog.categories.map((c) => (
            <label key={c.key} className="check">
              <input type="checkbox" checked={on(value.categories, c.key)} onChange={(e) => setCategory(c.key, e.target.checked)} />
              {c.color && <span className="feat-dot" style={{ background: c.color }} aria-hidden />}
              {tr(c.name, lang)}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="feat-group">
        <legend>{t('features.modules')}</legend>
        <div className="feat-grid">
          {catalog.modules.map((m) => {
            const parentOff = !!m.parent && !on(value.modules, m.parent);
            return (
              <label key={m.key} className="check" style={m.parent ? { paddingLeft: 22 } : undefined} title={t(`features.hint.${m.key}`)}>
                <input type="checkbox" checked={on(value.modules, m.key) && !parentOff} disabled={parentOff} onChange={(e) => setModule(m.key, e.target.checked)} />
                {t(`features.mod.${m.key}`)}
              </label>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}

/** Branche und Paket beim Anlegen eines Kunden (wählt Kategorie + allgemeine Module vor). */
export function NewTenantFeatures({ value, onChange }: { value: FeatureValue & { industry: string }; onChange: (v: FeatureValue & { industry: string }) => void }) {
  const { t, i18n } = useTranslation();
  const { data: catalog } = useFeatureCatalog();
  const [open, setOpen] = useState(false);
  const industries = (catalog?.categories ?? []).filter((c) => c.key !== 'basis');
  useEffect(() => {
    // Erste Anzeige: Paket der vorgewählten Branche
    if (catalog && !Object.keys(value.modules).length) onChange({ ...fromPreset(catalog, presetForIndustry(catalog, value.industry)), industry: value.industry });
  }, [catalog]);
  if (!catalog) return <Spinner />;
  return (
    <>
      <Field label={t('features.industry')} hint={t('features.industryHint')}>
        <select value={value.industry} aria-label={t('features.industry')}
          onChange={(e) => onChange({ ...fromPreset(catalog, presetForIndustry(catalog, e.target.value)), industry: e.target.value })}>
          {industries.map((c) => <option key={c.key} value={c.key}>{tr(c.name, i18n.language)}</option>)}
          <option value="andere">{t('features.otherIndustry')}</option>
        </select>
      </Field>
      <button type="button" className="btn sm ghost" style={{ alignSelf: 'flex-start' }} aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name="more" />{open ? t('features.hideDetails') : t('features.showDetails')}
      </button>
      {open && <FeatureToggles catalog={catalog} value={value} onChange={(v) => onChange({ ...v, industry: value.industry })} />}
    </>
  );
}

/** Dialog „Funktionen & Vorlagen" eines Kunden. */
export function TenantFeaturesDialog({ tenant, onClose }: { tenant: TenantListItem; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: catalog } = useFeatureCatalog();
  const { data: current } = useTenantFeatures(tenant.id);
  const [value, setValue] = useState<FeatureValue | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (current && !value) setValue({ preset: current.preset, modules: current.modules, categories: current.categories });
  }, [current, value]);
  const save = async () => {
    if (!value) return;
    setBusy(true);
    try {
      await api.put(`/tenants/${tenant.id}/features`, { ...(value.preset ? { preset: value.preset } : { preset: null }), modules: value.modules, categories: value.categories });
      await qc.invalidateQueries({ queryKey: qkFeatures.tenant(tenant.id) });
      toast.show(t('features.saved', { name: tenant.name }));
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog wide title={t('features.titleFor', { name: tenant.name })} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!value || busy} onClick={save}>{t('common.save')}</button>
    </>}>
      {!catalog || !value ? <Spinner /> : (
        <div className="col" style={{ gap: 12 }}>
          <div className="hint">{t('features.intro')}</div>
          {current && !current.configured && <div className="muted small">{t('features.notConfigured')}</div>}
          <FeatureToggles catalog={catalog} value={value} onChange={setValue} />
          <div className="muted small">{t('features.keepPlaying')}</div>
        </div>
      )}
    </Dialog>
  );
}

/** Globale Vorlagen „nur für Kunden: …" (z. B. eine eigens gestaltete Vorlage für einen Kunden). */
export function TemplateAccessDialog({ tenants, onClose }: { tenants: TenantListItem[]; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { data: items, isLoading } = useGlobalTemplates();
  const { data: catalog } = useFeatureCatalog();
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState<GlobalTemplateAccess | null>(null);
  const names = useMemo(() => new Map(tenants.map((x) => [x.id, x.name])), [tenants]);
  const catName = (c: string | null) => {
    const cat = catalog?.categories.find((x) => x.key === (c ?? 'basis'));
    return cat ? tr(cat.name, i18n.language) : '';
  };
  const list = (items ?? []).filter((i) => !q || `${i.name} ${catName(i.category)}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog wide title={t('features.access.title')} onClose={onClose} actions={<button className="btn primary" onClick={onClose}>{t('common.close')}</button>}>
      <div className="col" style={{ gap: 10 }}>
        <div className="hint">{t('features.access.intro')}</div>
        <input type="search" placeholder={t('common.search')} aria-label={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        {isLoading ? <Spinner /> : (
          <div className="scroll" style={{ maxHeight: 420 }}>
            <table className="list">
              <thead><tr><th>{t('common.name')}</th><th>{t('features.access.category')}</th><th>{t('features.access.visibleFor')}</th><th /></tr></thead>
              <tbody>
                {list.map((i) => (
                  <tr key={i.id}>
                    <td>{i.name}</td>
                    <td className="muted small">{catName(i.category)}</td>
                    <td>{i.tenantIds.length
                      ? <span className="pill warn plain">{t('features.access.only', { list: i.tenantIds.map((id) => names.get(id) ?? id.slice(0, 8)).join(', ') })}</span>
                      : <span className="muted small">{t('features.access.all')}</span>}</td>
                    <td style={{ textAlign: 'right' }}><button className="btn sm" onClick={() => setEdit(i)}><Icon name="edit" />{t('features.access.change')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {edit && <TemplateAccessEdit item={edit} tenants={tenants} onClose={() => setEdit(null)} />}
    </Dialog>
  );
}

function TemplateAccessEdit({ item, tenants, onClose }: { item: GlobalTemplateAccess; tenants: TenantListItem[]; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [only, setOnly] = useState(item.tenantIds.length > 0);
  const [sel, setSel] = useState<Set<string>>(new Set(item.tenantIds));
  const save = async () => {
    try {
      await api.put(`/platform/global-templates/${item.id}/tenants`, { tenantIds: only ? [...sel] : [] });
      await qc.invalidateQueries({ queryKey: qkFeatures.templates });
      onClose();
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={item.name} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={only && !sel.size} onClick={save}>{t('common.save')}</button>
    </>}>
      <div className="col" style={{ gap: 8 }}>
        <label className="check"><input type="radio" name="vis" checked={!only} onChange={() => setOnly(false)} />{t('features.access.all')}</label>
        <label className="check"><input type="radio" name="vis" checked={only} onChange={() => setOnly(true)} />{t('features.access.onlySelected')}</label>
        {only && (
          <div className="col" style={{ gap: 4, paddingLeft: 24, maxHeight: 260, overflow: 'auto' }}>
            {[...tenants].sort((a, b) => a.name.localeCompare(b.name)).map((x) => (
              <label key={x.id} className="check">
                <input type="checkbox" checked={sel.has(x.id)} onChange={(e) => { const n = new Set(sel); if (e.target.checked) n.add(x.id); else n.delete(x.id); setSel(n); }} />
                {x.name}
              </label>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}
